import { Log } from "@/utils";
import Tracking from "@/model/tracking.js";
import Kodo from "@/model/kodo.js";
import { SERVER_URL, SERVICE_URL } from "@/config.js";
import { useMidStore } from "@/store/midStore";
import { useUserStore } from "@/store/userStore";
import axiosInstance from "@/utils/axiosInterceptor.js";
import { v4 as uuidv4 } from "uuid";

// 创建一个函数，用于获取指定 URL 中的 redirect_switch 值
const fetchIsWebToMpRedirectEnable = async () => {
  try {
    const url = `${SERVICE_URL}/wechat/mp/redirect_switch`;
    const response = await axiosInstance.get(url, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    const isWebToMpRedirectEnable = response.data.redirect_switch;

    Log.success("fetchIsWebToMpRedirectEnable", isWebToMpRedirectEnable);
    return isWebToMpRedirectEnable;
  } catch (error) {
    Log.failure("fetchIsWebToMpRedirectEnable", error);
    throw error;
  }
};

// 获取小程序码 getUnlimitedQRCode
const getUnlimitedQRCode = async (page, scene) => {
  try {
    const url = `https://${SERVER_URL}/wx-get-qrcode`;
    const response = await axiosInstance.post(
      url,
      {
        scene: scene,
        page: page,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );

    return URL.createObjectURL(response.data);
  } catch (error) {
    console.log("getUnlimitedQRCode error", error);
  }
};

/**
 * 获取微信 URL Scheme 值
 * @param {string} page 小程序页面路径，默认为 'index'
 * @returns {Promise<string>} 返回 URL Scheme
 */
const getUrlScheme = async (page = "index") => {
  try {
    const url = `${SERVICE_URL}/wechat/mp/url_scheme?page=${encodeURIComponent(
      page
    )}`;
    const response = await axiosInstance.get(url);
    const urlScheme = response.data.url_scheme;

    Log.success("getUrlScheme", urlScheme);
    return urlScheme;
  } catch (error) {
    Log.failure("getUrlScheme", error);
    throw error;
  }
};

/**
 * 创建 EasyFile 对象
 * @param {*} ossUrl
 * @param {*} kodoKey
 * @param {*} name
 * @param {*} contentType
 * @param {*} size
 * @returns
 */
const createEasyFile = async (ossUrl, kodoKey, name, contentType, size) => {
  try {
    const midStore = useMidStore();
    const userStore = useUserStore();
    const mid = await midStore.useMid();

    const data = {
      kodo_key: kodoKey,
      name: name,
      content_type: contentType,
      size: size,
      machine_id: mid,
    };

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${SERVICE_URL}/easyfiles`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    if (userStore.token) {
      config.headers.Authorization = `Bearer ${userStore.token}`;
    }

    const response = await axiosInstance.request(config);
    Log.success("createEasyFile", response.data);
    return response.data;
  } catch (error) {
    Log.failure("createEasyFile", error);
    throw error;
  }
};

/**
 * 查询 EasyFile 的 url 是否在数据库中存在
 * @param {*} key
 * @returns
 */
const isEasyFileKeyExist = async (key) => {
  try {
    const config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${SERVICE_URL}/easyfiles?url=${key}`,
      headers: {},
    };

    const response = await axiosInstance.request(config);
    const data = response.data;

    // 检查返回的数组是否为空
    const result = Array.isArray(data) && data.length > 0;
    Log.success("isEasyFileKeyExist", result);
    return result;
  } catch (error) {
    Log.failure("isEasyFileKeyExist", error);
    throw error;
  }
};

/**
 * 获取 EasyFile 对象
 * @param {*} url
 */
const getEasyFile = async (url) => {
  try {
    // 配置请求
    const config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${SERVICE_URL}/easyfiles?url=${url}`,
      headers: {},
    };

    // 发送请求并处理响应
    const response = await axiosInstance.request(config);
    const result = response.data;

    Log.success("getEasyFile", result);
    return result;
  } catch (error) {
    Log.failure("getEasyFile", error);
    throw error;
  }
};

/**
 * 上传文件接口
 * @param {*} name
 * @param {*} file
 * @param {*} currentProgress
 * @returns
 */
const uploadFile = async (name, file, currentProgress) => {
  try {
    // 如果是 .md 文件但 type 未知，手动设置 MIME 类型
    const contentType =
      file.type === "" || file.type === "application/octet-stream"
        ? name.endsWith(".md")
          ? "text/markdown"
          : file.type
        : file.type;

    // 生成文件 Key
    const ossKey = `${uuidv4()}_${name}`;
    Log.success("ossKey", ossKey);

    Tracking.send({
      trigger: "file-upload-start",
      page: "main-page",
      extra: {
        file_name: ossKey,
        file_size: file.size,
      },
    });

    // upload file to object storage
    await Kodo.uploadFile(ossKey, file, currentProgress);

    // 创建 EasyFile 的记录
    const easyFile = await createEasyFile(
      "",
      ossKey,
      name,
      contentType,
      file.size
    );

    const easylinkUrl = `https://easylink.cc/${easyFile.url}`;

    const result = {
      name: name,
      url: easylinkUrl,
      object_id: easyFile.object_id,
    };

    Tracking.send({
      trigger: "file-upload-success",
      page: "main-page",
      extra: {
        file_name: ossKey,
        file_size: file.size,
      },
    });

    Log.success("uploadFile", result);
    return result;
  } catch (error) {
    Log.failure("uploadFile", error);
    throw error;
  }
};

/**
 * 创建 Report 记录
 * @param {string} easyfileId EasyFile 的 ID
 * @param {string} status 报告状态，默认为 "pending"
 * @returns {Promise<Object>} 创建的 Report 对象
 */
const createReport = async (easyfileId, status = "pending") => {
  try {
    const url = `${SERVICE_URL}/reports`;
    const response = await axiosInstance.post(
      url,
      {
        easyfile_id: easyfileId,
        status: status,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const result = response.data;
    Log.success("createReport", result);
    return result;
  } catch (error) {
    Log.failure("createReport", error);
    throw error;
  }
};

/**
 * 获取设备 ID
 * @returns
 */
const fetchMid = async () => {
  try {
    const response = await axiosInstance.post(`${SERVICE_URL}/machines`, {
      user_agent: navigator.userAgent,
      platform: navigator.platform,
      vendor: navigator.vendor,
      language: navigator.language,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      device_pixel: window.devicePixelRatio,
    });
    Log.success("fetchMid", response.data.object_id);
    return response.data.object_id;
  } catch (error) {
    Log.failure("fetchMid", error);
    throw error;
  }
};

/**
 * 获取登录二维码
 * @returns {Object} 返回登录二维码信息
 *           - scene_id {string} 场景ID
 *           - ticket {string} 微信二维码ticket
 *           - expire_seconds {number} 过期时间(秒)
 *           - qrcode_url {string} 二维码图片URL
 */
const getLoginQrcode = async () => {
  const response = await axiosInstance.get(
    "https://service.easylink.cc/wechat/osa/login/qrcode"
  );
  return response.data;
};

/**
 * 检查登录状态
 * @param {*} sceneId
 * @returns {Object} 返回登录状态信息
 *           - status {number} 状态码：0-未扫码，1-新关注用户，2-已关注用户，3-已过期
 *           - jwt_token {string} JWT令牌（仅当status=1或2时返回）
 *           - unionid {string} 用户唯一标识（仅当status=1或2时返回）
 */
const checkLoginStatus = async (sceneId) => {
  const response = await axiosInstance.get(
    `https://service.easylink.cc/wechat/osa/login/check?scene_id=${sceneId}`
  );
  return response.data;
};

/**
 * 验证 JWT Token 是否有效
 * @returns {Promise<boolean>} 如果没有 token 返回 true，有 token 时验证是否有效
 */
const verifyToken = async () => {
  const userStore = useUserStore();

  if (!userStore.token) return true;

  try {
    const response = await axiosInstance.get(`${SERVICE_URL}/auth/verify`, {
      headers: {
        Authorization: `Bearer ${userStore.token}`,
      },
    });
    return response.status === 200;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      // token 无效，清除登录状态
      userStore.logout();
    }
    return false;
  }
};

const getMyEasyFiles = async (page = 1, limit = 20) => {
  try {
    const config = {
      method: "get",
      url: `${SERVICE_URL}/easyfiles/@me?page=${page}&limit=${limit}`,
      headers: {
        Authorization: `Bearer ${useUserStore().token}`,
      },
    };

    const response = await axiosInstance.request(config);
    Log.success("getMyEasyFiles", response.data);
    return response.data;
  } catch (error) {
    Log.failure("getMyEasyFiles", error);
    throw error;
  }
};

const deleteEasyFiles = async (objectIds) => {
  try {
    const config = {
      method: "delete",
      url: `${SERVICE_URL}/easyfiles`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${useUserStore().token}`,
      },
      data: {
        object_ids: objectIds,
      },
    };

    const response = await axiosInstance.request(config);
    Log.success("deleteEasyFiles", response.data);
    return response.data;
  } catch (error) {
    Log.failure("deleteEasyFiles", error);
    throw error;
  }
};

const getMyTrashFiles = async (page = 1, limit = 20) => {
  try {
    const config = {
      method: "get",
      url: `${SERVICE_URL}/easyfiles/@me/trash?page=${page}&limit=${limit}`,
      headers: {
        Authorization: `Bearer ${useUserStore().token}`,
      },
    };

    const response = await axiosInstance.request(config);
    Log.success("getMyTrashFiles", response.data);
    return response.data;
  } catch (error) {
    Log.failure("getMyTrashFiles", error);
    throw error;
  }
};

const restoreEasyFiles = async (objectIds) => {
  try {
    const config = {
      method: "post",
      url: `${SERVICE_URL}/easyfiles/restore`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${useUserStore().token}`,
      },
      data: {
        object_ids: objectIds,
      },
    };

    const response = await axiosInstance.request(config);
    Log.success("restoreEasyFiles", response.data);
    return response.data;
  } catch (error) {
    Log.failure("restoreEasyFiles", error);
    throw error;
  }
};

const permanentDeleteEasyFiles = async (objectIds) => {
  try {
    const config = {
      method: "delete",
      url: `${SERVICE_URL}/easyfiles/permanent`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${useUserStore().token}`,
      },
      data: {
        object_ids: objectIds,
      },
    };

    const response = await axiosInstance.request(config);
    Log.success("permanentDeleteEasyFiles", response.data);
    return response.data;
  } catch (error) {
    Log.failure("permanentDeleteEasyFiles", error);
    throw error;
  }
};

const renameEasyFile = async (objectId, newName) => {
  try {
    const config = {
      method: "patch",
      url: `${SERVICE_URL}/easyfiles/${objectId}/name`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${useUserStore().token}`,
      },
      data: {
        name: newName,
      },
    };

    const response = await axiosInstance.request(config);
    Log.success("renameEasyFile", response.data);
    return response.data;
  } catch (error) {
    Log.failure("renameEasyFile", error);
    throw error;
  }
};

/**
 * 创建临时下载记录
 * @param {string} kodoKey - 七牛云文件key
 * @returns {Promise<Object>} 创建的临时下载记录
 */
const createTempDownload = async (kodoKey) => {
  try {
    const url = `${SERVICE_URL}/temp-downloads`;
    const response = await axiosInstance.post(
      url,
      {
        kodo_key: kodoKey,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    Log.success("createTempDownload", response.data);
    return response.data;
  } catch (error) {
    Log.failure("createTempDownload", error);
    throw error;
  }
};

const checkTempDownloadExists = async (kodoKey) => {
  try {
    const url = `${SERVICE_URL}/temp-downloads/check/${kodoKey}`;
    const response = await axiosInstance.get(url);
    Log.success("checkTempDownloadExists", response.data);
    return response.data.exists;
  } catch (error) {
    Log.failure("checkTempDownloadExists", error);
    throw error;
  }
};

// 添加获取下载链接的方法
const getDownloadUrl = async (kodoKey) => {
  try {
    // 直接获取 CDN 下载地址
    const cdnUrl = await Kodo.getCdnUrl(kodoKey);
    Log.success("getDownloadUrl", cdnUrl);
    return cdnUrl;
  } catch (error) {
    Log.failure("getDownloadUrl", error);
    throw error;
  }
};

const searchMyEasyFiles = async (keyword, page = 1, limit = 20) => {
  try {
    const config = {
      method: "get",
      url: `${SERVICE_URL}/easyfiles/@me/search?keyword=${encodeURIComponent(
        keyword
      )}&page=${page}&limit=${limit}`,
      headers: {
        Authorization: `Bearer ${useUserStore().token}`,
      },
    };

    const response = await axiosInstance.request(config);
    Log.success("searchMyEasyFiles", response.data);
    return response.data;
  } catch (error) {
    Log.failure("searchMyEasyFiles", error);
    throw error;
  }
};

/**
 * 转换临时文件为永久文件
 * @param {Object} params - 请求参数
 * @param {string[]} params.object_ids - 文件ID列表
 * @param {string} params.machine_id - 机器ID
 * @returns {Promise<Object>} 转换结果
 */
const convertTempFiles = async ({ object_ids, machine_id }) => {
  try {
    const url = `${SERVICE_URL}/easyfiles/convert`;
    const response = await axiosInstance.post(
      url,
      {
        object_ids,
        machine_id,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${useUserStore().token}`,
        },
      }
    );

    Log.success("convertTempFiles", response.data);
    return response.data;
  } catch (error) {
    Log.failure("convertTempFiles", error);
    throw error;
  }
};

const getDirectDownloadUrl = async (kodoKey, fileName) => {
  try {
    const encodedFileName = encodeURIComponent(fileName);
    const url = `${SERVICE_URL}/kodo/object/${kodoKey}/download?fileName=${encodedFileName}`;
    const response = await axiosInstance.get(url);
    Log.success("getDirectDownloadUrl", response.data);
    return response.data.download_url;
  } catch (error) {
    Log.failure("getDirectDownloadUrl", error);
    throw error;
  }
};

const checkMidExists = async (mid) => {
  try {
    const response = await axiosInstance.get(
      `${SERVICE_URL}/machines/${mid}/exists`
    );
    Log.success("checkMidExists", response.data);
    return response.data.exists;
  } catch (error) {
    Log.failure("checkMidExists", error);
    throw error;
  }
};

/**
 * 获取用户 IP 地址信息
 * @returns {Promise<string>} 用户的 IP 地址
 */
const fetchIpAddress = async () => {
  try {
    const url = `${SERVICE_URL}/ip-info`;
    const response = await axiosInstance.get(url, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    Log.success("fetchIpAddress", response.data);
    return response.data.ip;
  } catch (error) {
    Log.failure("fetchIpAddress", error);
    throw error;
  }
};

/**
 * 获取用户存储使用情况
 * @returns {Promise<Object>} 存储使用情况
 */
const getMyStorageUsage = async () => {
  try {
    const userStore = useUserStore();
    const config = {
      method: "get",
      url: `${SERVICE_URL}/easyfiles/@me/storage`,
      headers: {
        Authorization: `Bearer ${userStore.token}`,
      },
    };

    const response = await axiosInstance.request(config);
    Log.success("getMyStorageUsage", response.data);
    return response.data;
  } catch (error) {
    Log.failure("getMyStorageUsage", error);
    // 如果是401错误，也应该清除登录状态
    if (error.response && error.response.status === 401) {
      const userStore = useUserStore();
      userStore.logout();
    }
    throw error;
  }
};

/**
 * 获取会员计划
 * @returns {Promise<Array>} 会员计划列表
 */
const getMembershipPlans = async () => {
  try {
    const url = `${SERVICE_URL}/membership-plans`; // 使用 SERVICE_URL，因为 API 路径通常指向后端服务
    const response = await axiosInstance.get(url, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    Log.success("getMembershipPlans", response.data);
    return response.data;
  } catch (error) {
    Log.failure("getMembershipPlans", error);
    throw error;
  }
};

export default {
  uploadFile,
  createEasyFile,
  getEasyFile,
  getUrlScheme,
  getUnlimitedQRCode,
  fetchIsWebToMpRedirectEnable,
  createReport,
  fetchMid,
  checkMidExists,
  getLoginQrcode,
  checkLoginStatus,
  isEasyFileKeyExist,
  verifyToken,
  getMyEasyFiles,
  deleteEasyFiles,
  getMyTrashFiles,
  restoreEasyFiles,
  permanentDeleteEasyFiles,
  renameEasyFile,
  createTempDownload,
  checkTempDownloadExists,
  getDownloadUrl,
  searchMyEasyFiles,
  convertTempFiles,
  getDirectDownloadUrl,
  fetchIpAddress,
  getMyStorageUsage,
  getMembershipPlans,
};
